import React, { useState, useEffect, useContext } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Input, Checkbox } from '@components';
import { post } from '@utils/axios_api';
import { AuthContext } from '@context';
import { X, ChevronLeft } from 'lucide-react';

import '../../pages/user/Login.css';
import ForgotIdView from './ForgotIdView';
import ForgotPasswordView from './ForgotPasswordView';
import ChangePasswordView from './ChangePasswordView';

const LoginModal = ({ isOpen, onClose, initialStep = 'login', onLoginSuccess }) => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState('login'); // 'login' | 'forgot-id' | 'forgot-password' | 'change-password'
  const [serverError, setServerError] = useState('');

  const methods = useForm({
    defaultValues: { id: '', password: '', saveId: false },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      setServerError('');
      const isIdSaved = localStorage.getItem('isIdSaved') === 'true';
      if (isIdSaved) {
        const savedUserId = localStorage.getItem('savedUserId');
        if (savedUserId) setValue('id', savedUserId);
        setValue('saveId', true);
      }
    }
  }, [isOpen, initialStep, setValue]);

  const handleClose = () => {
    setStep('login');
    setServerError('');
    onClose();
  };

  const onSubmit = async (data) => {
    setServerError('');
    try {
      const loginData = { username: data.id, password: data.password };
      const loginInfo = await post('/login', loginData);

      if (data.saveId) {
        localStorage.setItem('savedUserId', data.id);
        localStorage.setItem('isIdSaved', 'true');
      } else {
        localStorage.removeItem('savedUserId');
        localStorage.setItem('isIdSaved', 'false');
      }

      login(loginInfo);
      reset();

      if (loginInfo.forceChangePw === 'Y') {
        setStep('change-password');
      } else {
        handleClose();
        if (onLoginSuccess) {
          onLoginSuccess(loginInfo);
        } else {
          navigate('/main');
        }
      }

    } catch (err) {
      setServerError(err.response?.data?.errorMessage || '로그인에 실패했습니다.');
    }
  };

  const isChangePwStep = step === 'change-password';

  return (
    <Modal
      show={isOpen}
      onHide={isChangePwStep ? undefined : handleClose}
      centered
      className="login-modal"
      style={{ '--bs-modal-width': '400px' }}
      backdrop={isChangePwStep ? 'static' : true}
      keyboard={!isChangePwStep}
    >
      <Modal.Header className="border-0 pb-0 d-flex align-items-center" style={{ minHeight: '52px' }}>
        {step !== 'login' && !isChangePwStep && (
          <button
            onClick={() => setStep('login')}
            className="bg-transparent border-0 p-2"
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.9rem', color: '#fff' }}
          >
            <ChevronLeft size={18} />
            뒤로
          </button>
        )}
        {!isChangePwStep && (
          <button
            onClick={handleClose}
            className="ms-auto bg-transparent border-0 p-2 transition-colors"
            style={{ color: '#fff' }}
          >
            <X size={20} />
          </button>
        )}
      </Modal.Header>

      <Modal.Body className="px-4 py-4" style={{ minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
        {step === 'login' && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="login-form" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* 로고 영역 */}
              <div className="logo text-center mb-3">
                <img src="/img/logo.png" alt="회사 로고" style={{ width: '100px', height: 'auto' }} />
              </div>

              <div className="text-center mb-3">
                <h1 className="fs-4 fw-bold text-gray-800">중대재해 예방 플랫폼</h1>
                {serverError && <p className="text-danger mt-2 small">{serverError}</p>}
              </div>

              <div className="login_input_wrap mb-3">
                <Input
                  name="id"
                  type="text"
                  placeholder="아이디를 입력해 주세요."
                  required={true}
                  maxLength="30"
                />
                <Input
                  name="password"
                  type="password"
                  placeholder="비밀번호를 입력해 주세요."
                  required={true}
                  maxLength="20"
                />
              </div>

              <div className="remember-me mb-3 d-flex justify-content-start">
                <Checkbox
                  label="아이디 저장"
                  name="saveId"
                  required={false}
                />
              </div>

              <button
                type="submit"
                className="login-button w-100 py-2 fs-5 fw-bold"
                disabled={isSubmitting}
                style={{ borderRadius: '8px' }}
              >
                {isSubmitting ? '로그인 중...' : '로그인'}
              </button>

              {/* 아이디/비밀번호 찾기 링크 */}
              <div className="d-flex justify-content-center gap-3 mt-auto pt-3 small text-gray-500">
                <button
                  type="button"
                  onClick={() => setStep('forgot-id')}
                  className="bg-transparent border-0 text-gray-500 p-0"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  아이디 찾기
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => setStep('forgot-password')}
                  className="bg-transparent border-0 text-gray-500 p-0"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  비밀번호 찾기
                </button>
              </div>
            </form>
          </FormProvider>
        )}

        {step === 'forgot-id' && (
          <ForgotIdView
            onGoLogin={() => setStep('login')}
            onGoForgotPw={() => setStep('forgot-password')}
          />
        )}

        {step === 'forgot-password' && (
          <ForgotPasswordView
            onGoLogin={() => setStep('login')}
            onGoForgotId={() => setStep('forgot-id')}
          />
        )}

        {step === 'change-password' && (
          <ChangePasswordView
            onSuccess={() => { handleClose(); navigate('/main'); }}
            onClose={handleClose}
          />
        )}

      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
